"use strict";
// Acquisition attribution in closed vocabularies (ARCH-002 amendment
// *Closed-vocabulary attribution and operator-registered links*, PRD-1177).
//
// Nothing a visitor types is ever persisted: a registration resolves to a value
// of one of the vocabularies below or to the code of an operator-registered
// attribution link, and whatever fails a guard is dropped — never refused,
// because attribution is a measurement, not a gate. Counted by IN-016; no
// product behaviour may branch on any of it.
Object.defineProperty(exports, "__esModule", { value: true });
exports.COMPANY_SIZES = exports.INDUSTRIES = exports.SELF_REPORTED_SOURCES = exports.REFERRER_CLASSES = exports.ATTRIBUTION_LINK_CODE_LENGTH = exports.LINK_CHANNELS = exports.ACQUISITION_CHANNELS = void 0;
exports.isAcquisitionChannel = isAcquisitionChannel;
exports.isLinkChannel = isLinkChannel;
exports.isAttributionLinkCode = isAttributionLinkCode;
exports.generateAttributionLinkCode = generateAttributionLinkCode;
exports.isReferrerClass = isReferrerClass;
exports.resolveAttribution = resolveAttribution;
exports.isSelfReportedSource = isSelfReportedSource;
exports.isIndustry = isIndustry;
exports.isCompanySize = isCompanySize;
/**
 * The one dimension every channel figure groups by. The last three are
 * derived-only: they describe a visit nobody registered a link for, so an
 * operator must never be able to assign them to a link.
 */
exports.ACQUISITION_CHANNELS = [
    'website',
    'einvoice-tool',
    'tax-advisor',
    'partner',
    'search-ads',
    'social-ads',
    'social',
    'email',
    'event',
    'print',
    'referral',
    'organic-search',
    'direct',
    'unattributed',
];
/** Type guard: is `value` one of the acquisition channels? */
function isAcquisitionChannel(value) {
    return typeof value === 'string' && exports.ACQUISITION_CHANNELS.includes(value);
}
/** The channels derived from a visit; never assignable to a link. */
const DERIVED_ONLY_CHANNELS = ['organic-search', 'direct', 'unattributed'];
/** The channels an operator may give an attribution link (ARCH-002 point 6). */
exports.LINK_CHANNELS = exports.ACQUISITION_CHANNELS.filter((channel) => !DERIVED_ONLY_CHANNELS.includes(channel));
/** Type guard: may an operator assign `value` to a link? */
function isLinkChannel(value) {
    return typeof value === 'string' && exports.LINK_CHANNELS.includes(value);
}
/**
 * A link code: eight characters of the lowercase Crockford base-32 alphabet
 * (no i, l, o, u — nothing a flyer's typeface can confuse), generated on the
 * server, never chosen by a caller (DATA-025 Decision 5). The format is
 * checked BEFORE any lookup, so an arbitrary string never reaches a query.
 */
exports.ATTRIBUTION_LINK_CODE_LENGTH = 8;
const CODE_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
const CODE_PATTERN = new RegExp(`^[${CODE_ALPHABET}]{${exports.ATTRIBUTION_LINK_CODE_LENGTH}}$`);
/** Type guard: is `value` a well-formed attribution link code? */
function isAttributionLinkCode(value) {
    return typeof value === 'string' && CODE_PATTERN.test(value);
}
/**
 * A fresh code from `random` — a function in `[0, 1)`, `Math.random` in
 * production, a constant in a spec. Uniqueness is the store's job (a
 * transactional create that retries on collision), not the generator's.
 */
function generateAttributionLinkCode(random) {
    let code = '';
    for (let i = 0; i < exports.ATTRIBUTION_LINK_CODE_LENGTH; i++) {
        const index = Math.min(CODE_ALPHABET.length - 1, Math.max(0, Math.floor(random() * CODE_ALPHABET.length)));
        code += CODE_ALPHABET[index];
    }
    return code;
}
/**
 * What the public page derives from a visit it saw arrive: the paid classes
 * from the mere presence of an ad network's click identifier, the rest from
 * the referring host. The host itself is never sent.
 */
exports.REFERRER_CLASSES = [
    'paid-search',
    'paid-social',
    'organic-search',
    'social',
    'referral',
    'direct',
];
/** Type guard: is `value` one of the referrer classes? */
function isReferrerClass(value) {
    return typeof value === 'string' && exports.REFERRER_CLASSES.includes(value);
}
/**
 * The one resolution, with the precedence *active link > referrer class >
 * entry surface > `unattributed`* (ARCH-002 point 7).
 */
function resolveAttribution(inputs) {
    const facts = {};
    if (inputs.referrerClass)
        facts.referrer = inputs.referrerClass;
    if (inputs.source)
        facts.source = inputs.source;
    if (inputs.link?.active) {
        return { channel: inputs.link.channel, linkCode: inputs.link.code, ...facts };
    }
    if (inputs.referrerClass) {
        return { channel: channelOfReferrer(inputs.referrerClass), ...facts };
    }
    if (inputs.source) {
        return { channel: inputs.source, ...facts };
    }
    return { channel: 'unattributed' };
}
/** A visit the page classified, mapped onto the channel dimension. */
function channelOfReferrer(referrer) {
    switch (referrer) {
        case 'paid-search':
            return 'search-ads';
        case 'paid-social':
            return 'social-ads';
        default:
            return referrer;
    }
}
/**
 * The tenant's own statements (ARCH-002 point 9), answered optionally in
 * onboarding from closed lists and never merged into the measured
 * attribution. `SELF_REPORTED_SOURCES` answers "how did you hear about us";
 * the industry groups are broad and industry-agnostic on purpose — the product
 * does not branch on them, they exist to be counted.
 */
exports.SELF_REPORTED_SOURCES = [
    'tax-advisor',
    'recommendation',
    'search-engine',
    'social-media',
    'event',
    'press',
    'other',
];
function isSelfReportedSource(value) {
    return typeof value === 'string' && exports.SELF_REPORTED_SOURCES.includes(value);
}
exports.INDUSTRIES = [
    'trades',
    'construction',
    'consulting',
    'it-and-software',
    'creative-and-media',
    'retail-and-ecommerce',
    'wholesale',
    'manufacturing',
    'health-and-care',
    'hospitality',
    'real-estate',
    'education',
    'other',
];
function isIndustry(value) {
    return typeof value === 'string' && exports.INDUSTRIES.includes(value);
}
/** Headcount bands. */
exports.COMPANY_SIZES = ['1', '2-4', '5-9', '10-19', '20-49', '50+'];
function isCompanySize(value) {
    return typeof value === 'string' && exports.COMPANY_SIZES.includes(value);
}
//# sourceMappingURL=attribution.js.map